<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';

const props = withDefaults(
  defineProps<{
    as?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'text';
    small?: boolean;
    type?: 'button' | 'submit' | 'reset';
    to?: RouteLocationRaw;
    disabled?: boolean;
  }>(),
  {
    as: undefined,
    small: false,
    to: undefined,
    disabled: false,
    type: 'button',
  },
);

const emit = defineEmits<{
  (e: 'click', event: Event): void;
}>();

const padding = computed(() => {
  if ([undefined, 'text'].includes(props.as)) {
    return undefined;
  }

  const value = props.small
    ? {
        x: '0.6rem',
        y: '0.2rem',
      }
    : {
        x: '1rem',
        y: '0.5rem',
      };

  return `${value.y} ${value.x}`;
});
</script>

<template>
  <Component
    :is="to === undefined ? 'button' : RouterLink"
    :to="to"
    :class="[as]"
    :type="to === undefined ? type : undefined"
    :style="{
      padding,
    }"
    class="tml-button"
    :disabled="disabled || undefined"
    @click="emit('click', $event)"
  >
    <slot />
  </Component>
</template>

<style scoped>
.tml-button.primary {
  @apply rounded-[--tml-button-radius];
  @apply text-[--tml-button-primary-text-color] shadow hover:shadow-md text-center font-bold;
  @apply bg-[--tml-button-primary-background-color] hover:bg-[--tml-button-primary-hover-background-color];
  @apply outline -outline-offset-1 outline-[length:--tml-button-primary-border-width] outline-[--tml-button-primary-border-color];
  @apply focus:outline-[--tml-button-primary-active-border-color];
}
.tml-button.secondary {
  @apply rounded-[--tml-button-radius];
  @apply text-[--tml-button-secondary-text-color] shadow hover:shadow-md text-center font-bold;
  @apply bg-[--tml-button-secondary-background-color] hover:bg-[--tml-button-secondary-hover-background-color];
  @apply outline -outline-offset-1 outline-[length:--tml-button-secondary-border-width] outline-[--tml-button-secondary-border-color];
  @apply focus:outline-[--tml-button-secondary-active-border-color];
}
.tml-button.success {
  @apply rounded-[--tml-button-radius];
  @apply text-[--tml-button-success-text-color] shadow hover:shadow-md text-center font-bold;
  @apply bg-[--tml-button-success-background-color] hover:bg-[--tml-button-success-hover-background-color];
  @apply outline -outline-offset-1 outline-[length:--tml-button-success-border-width] outline-[--tml-button-success-border-color];
  @apply focus:outline-[--tml-button-success-active-border-color];
}
.tml-button.warning {
  @apply rounded-[--tml-button-radius];
  @apply text-[--tml-button-warning-text-color] shadow hover:shadow-md text-center font-bold;
  @apply bg-[--tml-button-warning-background-color] hover:bg-[--tml-button-warning-hover-background-color];
  @apply outline -outline-offset-1 outline-[length:--tml-button-warning-border-width] outline-[--tml-button-warning-border-color];
  @apply focus:outline-[--tml-button-warning-active-border-color];
}
.tml-button.danger {
  @apply rounded-[--tml-button-radius];
  @apply text-[--tml-button-danger-text-color] shadow hover:shadow-md text-center font-bold;
  @apply bg-[--tml-button-danger-background-color] hover:bg-[--tml-button-danger-hover-background-color];
  @apply outline -outline-offset-1 outline-[length:--tml-button-danger-border-width] outline-[--tml-button-danger-border-color];
  @apply focus:outline-[--tml-button-danger-active-border-color];
}
.tml-button.text {
  @apply text-link rounded-[--tml-button-radius];
  @apply text-[--tml-link-text-color] hover:text-[--tml-link-hover-text-color];
}
.tml-button {
  @apply cursor-pointer transition-shadow;
  @apply focus:outline-2;
  @apply transition-opacity;
}
a.tml-button {
  @apply block;
}

.tml-button[disabled] {
  @apply cursor-default;
  @apply pointer-events-none;
  @apply opacity-50;
}
</style>
